import { Badge, Button, Input, Popover, Select, Space, Typography } from 'antd'
import { FilterOutlined, SearchOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import './KnowledgeAdvancedSearch.css'

export type KnowledgeListFilters = {
  q: string
  relations_status?: string
  quality_tier?: string
  redundancy_tier?: string
}

const relationsFilterOptions = [
  { value: 'not_extracted', label: '未抽取' },
  { value: 'extracted', label: '已抽取' },
]

const qualityFilterOptions = [
  { value: 'high', label: '高质量' },
  { value: 'medium', label: '中质量' },
  { value: 'low', label: '低质量' },
  { value: 'unknown', label: '未评' },
]

const redundancyFilterOptions = [
  { value: 'none', label: '无冗余' },
  { value: 'light', label: '轻度冗余' },
  { value: 'heavy', label: '高度冗余' },
  { value: 'unknown', label: '未评' },
]

type Props = {
  value: KnowledgeListFilters
  onChange: (next: KnowledgeListFilters) => void
  onSearch: (next?: KnowledgeListFilters) => void
}

function activeFilterCount(filters: KnowledgeListFilters): number {
  let n = 0
  if (filters.relations_status) n += 1
  if (filters.quality_tier) n += 1
  if (filters.redundancy_tier) n += 1
  return n
}

export function KnowledgeAdvancedSearch({ value, onChange, onSearch }: Props) {
  const [draft, setDraft] = useState(value)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setDraft(value)
  }, [value])

  const filterCount = useMemo(() => activeFilterCount(value), [value])
  const draftFilterCount = useMemo(() => activeFilterCount(draft), [draft])

  const syncDraftFromValue = () => setDraft(value)

  const applySearch = (next?: KnowledgeListFilters) => {
    const payload = next ?? draft
    onChange(payload)
    onSearch(payload)
    setOpen(false)
  }

  const resetFilters = () => {
    const next = { q: draft.q }
    setDraft(next)
  }

  const filterPanel = (
    <div className="knowledge-advanced-search-panel">
      <Typography.Text type="secondary" className="knowledge-advanced-search-panel-title">
        筛选条件
      </Typography.Text>
      <Select
        allowClear
        placeholder="图谱抽取"
        style={{ width: '100%' }}
        value={draft.relations_status}
        options={relationsFilterOptions}
        onChange={(v) => setDraft((prev) => ({ ...prev, relations_status: v }))}
      />
      <Select
        allowClear
        placeholder="质量"
        style={{ width: '100%' }}
        value={draft.quality_tier}
        options={qualityFilterOptions}
        onChange={(v) => setDraft((prev) => ({ ...prev, quality_tier: v }))}
      />
      <Select
        allowClear
        placeholder="冗余"
        style={{ width: '100%' }}
        value={draft.redundancy_tier}
        options={redundancyFilterOptions}
        onChange={(v) => setDraft((prev) => ({ ...prev, redundancy_tier: v }))}
      />
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button size="small" onClick={resetFilters} disabled={draftFilterCount === 0}>
          清除筛选
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={() => applySearch()}
        >
          应用并搜索
        </Button>
      </Space>
    </div>
  )

  return (
    <Space.Compact className="knowledge-advanced-search">
      <Input
        allowClear
        placeholder="搜索文档标题"
        style={{ width: 240 }}
        value={draft.q}
        onChange={(e) => setDraft((prev) => ({ ...prev, q: e.target.value }))}
        onPressEnter={() => applySearch()}
      />
      <Popover
        trigger="click"
        placement="bottomRight"
        open={open}
        onOpenChange={(v) => {
          setOpen(v)
          if (v) syncDraftFromValue()
        }}
        content={filterPanel}
        overlayClassName="knowledge-advanced-search-popover"
      >
        <Badge count={filterCount || undefined} size="small" offset={[-4, 4]}>
          <Button icon={<FilterOutlined />} aria-label="高级筛选" title="高级筛选" />
        </Badge>
      </Popover>
      <Button type="primary" icon={<SearchOutlined />} onClick={() => applySearch()}>
        搜索
      </Button>
    </Space.Compact>
  )
}
